import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  BookOpen, 
  Users, 
  Music, 
  Mic, 
  Sparkles, 
  Shirt,
  Video,
  Gamepad2,
  CheckCircle,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SEO, { createServiceSchema, createBreadcrumbSchema } from '../components/SEO';
import { programs } from '../data/mock';

const iconMap = {
  BookOpen,
  Users,
  Music,
  Mic,
  Sparkles,
  Shirt,
  Video,
  Gamepad2,
};

const Programs = () => {
  // Structured data for programs/services
  const programsSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Mentors in Motion Programs",
    "description": "Youth development programs including tutoring, mentoring, and creative learning",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "Tutoring Program",
          "description": "Academic support and tutoring services for St. Louis youth"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "Youth Mentoring",
          "description": "One-on-one mentoring relationships with caring adult mentors"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "Music Production",
          "description": "Creative learning through music production and audio engineering"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Service",
          "name": "Apparel Design",
          "description": "Fashion and apparel design education for young creatives"
        }
      }
    ]
  };

  return (
    <div>
      <SEO 
        title="Youth Programs - Tutoring & Mentoring"
        description="Explore youth programs in St. Louis: tutoring, mentoring, music production, apparel design, and creative learning for ages Pre-K to 12th grade."
        keywords="youth tutoring St. Louis, mentoring programs Missouri, after school programs, music production classes, creative learning St. Louis, youth development programs"
        canonicalUrl="/programs"
        structuredData={programsSchema}
      />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(227,27,35,0.3),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Our Programs
            </h1>
            <p className="text-base text-gray-300 leading-relaxed">
              From tutoring and mentoring to creative learning tracks, our programs give scholars the academic support, life skills, and real-world exposure they need to thrive. Students strengthen reading and math, build confidence with caring adult mentors, and explore talents in music, media, fashion, cosmetology, podcasting, and gaming. Together, these experiences help young people see new possibilities for themselves, their education, and their future careers.
            </p>
          </div>
        </div>
      </section>

      {/* Creative Learning */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Creative Learning Tracks
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Unlock Your Creative Potential
            </h3>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our innovative Creative Learning programs tap into student talents while building real-world skills and entrepreneurial thinking.
            </p>
          </div>

          <Tabs defaultValue="music" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent h-auto">
              {programs.slice(2).map((program) => {
                const IconComponent = iconMap[program.icon];
                return (
                  <TabsTrigger
                    key={program.id}
                    value={program.id}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl data-[state=active]:bg-red-600 data-[state=active]:text-white bg-white shadow-sm hover:bg-gray-100 transition-colors"
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="hidden sm:inline">{program.title}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {programs.slice(2).map((program) => {
              const IconComponent = iconMap[program.icon];
              return (
                <TabsContent key={program.id} value={program.id}>
                  <div className="grid lg:grid-cols-2 gap-12 items-start">
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{program.title}</h3>
                          <p className="text-gray-500">{program.shortDesc}</p>
                        </div>
                      </div>
                      
                      <div className="text-gray-600 leading-relaxed mb-8 whitespace-pre-line">
                        {program.description}
                      </div>
                      
                      <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h4 className="font-semibold text-gray-900 mb-4">What You'll Learn:</h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {program.outcomes.map((outcome, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{outcome}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="relative">
                      {program.videoEmbed ? (
                        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
                          <div className="relative" style={{ paddingBottom: '56.25%' }}>
                            <iframe 
                              className="absolute inset-0 w-full h-full"
                              src={program.videoEmbed}
                              title={program.title}
                              frameBorder="0" 
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                              referrerPolicy="strict-origin-when-cross-origin" 
                              allowFullScreen
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-red-600/10 rounded-3xl transform rotate-3" />
                          <img
                            src={program.image || `https://images.unsplash.com/photo-${program.id === 'cosmetology' ? '1560066984-138dadb4c035' : program.id === 'apparel' ? '1558618666-fcd25c85cd64' : program.id === 'videography' ? '1492691527719-9d1e07e534b4' : '1542751371-adc38448a05e'}?w=600&h=450&fit=crop`}
                            alt={program.title}
                            className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* Core Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-3">
              Core Programs
            </h2>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Building Strong Foundations
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our tutoring and mentoring programs form the foundation of everything we do, providing essential academic and personal support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {programs.slice(0, 2).map((program) => {
              const IconComponent = iconMap[program.icon];
              return (
                <Card key={program.id} className="border-0 shadow-xl overflow-hidden">
                  <CardHeader className="bg-gray-50 p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-7 h-7 text-red-600" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl mb-2">{program.title}</CardTitle>
                        <p className="text-gray-600">{program.shortDesc}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {program.description}
                    </p>
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900">Key Outcomes:</h4>
                      {program.outcomes.map((outcome, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
    </div>
  );
};

export default Programs;
